import '@vuepic/vue-datepicker/dist/main.css';
import 'mdui/mdui.css';
import './assets/main.css';

import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';
import { setupI18n } from './helpers/i18n';
import VueCookies from 'vue-cookies';

import { loadLocale } from 'mdui/functions/loadLocale.js';
import { setLocale } from 'mdui/functions/setLocale.js';

import AxiosFactory from '@/services/axiosFactory';

import App from './App.vue';
import router from './router';
import { useStore } from '@/store';

const app = createApp(App);
const store = createPinia();

import InfoService from '@/services/infoService';

// Services
const infoService = new InfoService();

async function validateSession() {
    try {
        await infoService.Validate();
    } catch (error: any) {
        if (error.response && error.response.status === 401) {
            console.warn("User permission revoked. Logging out...");
            localStorage.removeItem("store");

            // Reset the in-memory store state
            const rootStore = useStore(store);
            rootStore.currentUser = undefined;
            rootStore.currentDebitor = undefined;

            router.push({
                path: "/login",
                query: { force: "true" }
            });
        }
    }
}

// STORE SETUP
if (localStorage.getItem('store')) {
    store.state.value = JSON.parse(localStorage.getItem('store')!);

    // Pass the 'store' instance to useStore to satisfy Pinia requirements outside components
    const rootStore = useStore(store);

    // Refresh from DOM because localStorage might contain stale language lists
    rootStore.refreshLanguages();

    if (store.state.value.store.version !== import.meta.env.PACKAGE_VERSION) {
        store.state.value = {};
    }
}

watch(
    store.state,
    (state) => {
        localStorage.setItem('store', JSON.stringify(state));
    },
    { deep: true }
);

// Translations
const lang = store.state.value?.store?.lang ?? document.documentElement.getAttribute('lang') ?? 'da-DK';
AxiosFactory.setLanguage(lang);
const i18n = setupI18n(lang);

// Locale handling for MDUI internals
loadLocale((locale) => import(`../node_modules/mdui/locales/${locale}.js`));

// If your language isn't supported by MDUI, we default to 'en-us'.
const mduiSupportedLocales = ['en-us', 'zh-cn', 'zh-tw'];
const normalizedLang = lang.toLowerCase();
const mduiLocale = mduiSupportedLocales.includes(normalizedLang) ? normalizedLang : 'en-us';

try {
    setLocale(mduiLocale);
} catch (e) {
    setLocale('en-us');
}

// CONFIGURE APP
app.use(VueCookies);
app.use(store);
app.use(router);
app.use(i18n);

if (localStorage.getItem("store")) {
    validateSession();
}

// MOUNT APP
app.mount('#app');

// Background check every 30 seconds
if (localStorage.getItem("store")) {
    setInterval(validateSession, 30000);
}
